// Metadata das tabelas de incidência: colunas do grid + campos do form, por tabela.
// `key` = slug do backend/rota (/configuracoes/tabelas/incidencias/{key} e /admin/tabelas/:tabela).
export type TipoCampo = 'text' | 'textarea' | 'date' | 'select';

export interface ColunaSpec {
  key: string;
  header: string;
  width?: number;
  format?: (value: unknown) => string;
}
export interface CampoSpec {
  key: string;
  label: string;
  type: TipoCampo;
  options?: { value: string; label: string }[];
  boolean?: boolean;
  required?: boolean;
}

export interface IncidenciaSpec {
  key: string;
  titulo: string;
  subtitulo: string;
  permWrite: string; // permissão de escrita da tabela
  colunasExtra: ColunaSpec[]; // além de code/name/notes/atualização
  campos: CampoSpec[]; // form completo (inclui code/name/notes)
  exportAssincrono?: boolean; // export persistido (report_process + S3) em vez de blob
}

const BASE_CAMPOS: CampoSpec[] = [
  { key: 'code', label: 'Código', type: 'text' },
  { key: 'name', label: 'Nome', type: 'text' },
  { key: 'notes', label: 'Observações', type: 'textarea' },
];

const IMPACTO_COLUNA: ColunaSpec = {
  key: 'impacto_em_tributacao',
  header: 'Impacto em tributação',
  width: 170,
  format: (v) => (v ? 'Sim' : 'Não'),
};
const IMPACTO_CAMPO: CampoSpec = {
  key: 'impacto_em_tributacao',
  label: 'Impacto em tributação',
  type: 'select',
  boolean: true,
  required: true,
  options: [
    { value: 'true', label: 'Sim' },
    { value: 'false', label: 'Não' },
  ],
};

export const INCIDENCIA_SPECS: Record<string, IncidenciaSpec> = {
  fgts: {
    key: 'fgts',
    titulo: 'Incidências para o FGTS',
    subtitulo: 'Configuração das incidências de FGTS por rubrica',
    permWrite: 'egov:config.tabelas-incidencias:write',
    colunasExtra: [IMPACTO_COLUNA],
    campos: [...BASE_CAMPOS, IMPACTO_CAMPO],
  },
  cprp: {
    key: 'cprp',
    titulo: 'Incidências para a CPRP',
    subtitulo: 'Configuração das incidências de CPRP por rubrica',
    permWrite: 'egov:config.tabelas-incidencias:write',
    colunasExtra: [IMPACTO_COLUNA],
    campos: [...BASE_CAMPOS, IMPACTO_CAMPO],
  },
  inss: {
    key: 'inss',
    titulo: 'Incidências para a CP',
    subtitulo: 'Configuração das incidências de CP (INSS) por rubrica',
    permWrite: 'egov:config.tabelas-incidencias:write',
    colunasExtra: [{ key: 'group', header: 'Grupo', width: 160 }, IMPACTO_COLUNA],
    campos: [...BASE_CAMPOS, { key: 'group', label: 'Grupo', type: 'text' }, IMPACTO_CAMPO],
  },
  irrf: {
    key: 'irrf',
    titulo: 'Incidências para o IRRF',
    subtitulo: 'Configuração das incidências de IRRF por rubrica',
    permWrite: 'egov:config.tabelas-incidencias:write',
    colunasExtra: [
      { key: 'group', header: 'Grupo', width: 160 },
      { key: 'situation', header: 'Situação', width: 120 },
      { key: 'start', header: 'Início', width: 120 },
      { key: 'end', header: 'Término', width: 120 },
      IMPACTO_COLUNA,
    ],
    campos: [
      ...BASE_CAMPOS,
      { key: 'group', label: 'Grupo', type: 'text' },
      { key: 'situation', label: 'Situação', type: 'text' },
      { key: 'start', label: 'Início', type: 'date' },
      { key: 'end', label: 'Término', type: 'date' },
      IMPACTO_CAMPO,
    ],
  },
  pispasep: {
    key: 'pispasep',
    titulo: 'Incidências para o PIS/PASEP',
    subtitulo: 'Configuração das incidências de PIS/PASEP por rubrica',
    permWrite: 'egov:config.tabelas-incidencias:write',
    colunasExtra: [
      { key: 'start_date', header: 'Início', width: 120 },
      { key: 'end_date', header: 'Término', width: 120 },
      IMPACTO_COLUNA,
    ],
    campos: [
      ...BASE_CAMPOS,
      { key: 'start_date', label: 'Início', type: 'date' },
      { key: 'end_date', label: 'Término', type: 'date' },
      IMPACTO_CAMPO,
    ],
  },
  natureza: {
    key: 'natureza',
    titulo: 'Natureza das Rubricas',
    subtitulo: 'Configuração da natureza das rubricas da folha',
    permWrite: 'egov:config.tabelas-natureza-rubrica:write',
    exportAssincrono: true,
    colunasExtra: [
      { key: 'desc', header: 'Descrição', width: 260 },
      { key: 'situation', header: 'Situação', width: 120 },
      { key: 'start', header: 'Início', width: 120 },
      { key: 'end', header: 'Término', width: 120 },
      { key: 'update_info', header: 'Info. atualização', width: 200 },
    ],
    campos: [
      { key: 'code', label: 'Código', type: 'text' },
      { key: 'name', label: 'Nome', type: 'text' },
      { key: 'desc', label: 'Descrição', type: 'textarea' },
      { key: 'situation', label: 'Situação', type: 'text' },
      { key: 'start', label: 'Início', type: 'date' },
      { key: 'end', label: 'Término', type: 'date' },
      { key: 'update_info', label: 'Info. atualização', type: 'textarea' },
      { key: 'notes', label: 'Observações', type: 'textarea' },
    ],
  },
  tipo: {
    key: 'tipo',
    titulo: 'Tipos de Rubrica',
    subtitulo: 'Configuração dos tipos de rubrica',
    permWrite: 'egov:config.tabelas-tipo-rubrica:write',
    colunasExtra: [],
    campos: [
      { key: 'code', label: 'Código', type: 'text' },
      { key: 'name', label: 'Nome', type: 'text' },
      { key: 'notes', label: 'Observações', type: 'textarea' },
    ],
  },
};

// slug do hub (config_tables) -> key da tabela
export const HUB_SLUG_TO_KEY: Record<string, string> = {
  fgts_incidence: 'fgts',
  cprp_incidence: 'cprp',
  inss_incidence: 'inss',
  irrf_incidence: 'irrf',
  pispasep_incidence: 'pispasep',
  rubric_nature: 'natureza',
  rubric_type: 'tipo',
};

export function getIncidenciaSpec(key: string | undefined): IncidenciaSpec | undefined {
  return key ? INCIDENCIA_SPECS[key] : undefined;
}
